/** The policy surface's routes: H2's refund sheet, and the four public documents. */
import type { RouteObject } from 'react-router-dom';
import { withSurfaceStates } from '../../states/boundary.js';
import { H2Refund } from './H2Refund.js';
import { PolicyDocumentScreen } from './PolicyDocument.js';
import { PolicyIndex } from './PolicyIndex.js';

/**
 * WP-K · state branches. `withSurfaceStates` wraps every element of this group in the
 * boundary for its matrix column; see `apps/web/src/states/boundary.tsx`. It changes
 * nothing about the healthy render — no state resolves unless an API error body says so,
 * so the screens below are untouched and their fidelity gate still measures what it
 * measured.
 *
 * ── TWO GROUPS, TWO SURFACES, AND THAT IS NOT AN OVERSIGHT ──────────────────────
 * H2 is `transaction` (লেনদেন): it is a sheet that opens over checkout, from a quote,
 * about an order's money. The four public documents are `onboarding`: they are read
 * BEFORE there is an account, reached from the signup screen's own sentence, and a
 * signed-out visitor is the case they exist for.
 *
 * The column decides which of the eleven states can appear. Putting the documents on the
 * transaction column would admit `payment_failed` on a page with no payment on it, and
 * would deny nothing useful in exchange. `applies()` enforces the choice either way —
 * the point of making it deliberately is that the matrix is the design's statement about
 * what can happen where, and a route group that picks its column by copying the line
 * above it is not reading that statement.
 */
export const policyRoutes: RouteObject[] = [
  ...withSurfaceStates('transaction', [{ path: '/policy/refund/:key', element: <H2Refund /> }]),
  ...withSurfaceStates('onboarding', [
    { path: '/policy', element: <PolicyIndex /> },
    /**
     * `:slug` and not four literal paths.
     *
     * Four paths would put the slug list in a second place — here and in
     * `POLICY_SLUGS` — and the second copy is the one that would be missing the fifth
     * document. The screen validates the parameter against the closed set and renders
     * nothing for a slug that is not in it, rather than asking the server about a path
     * it already knows is not a document.
     */
    { path: '/policy/:slug', element: <PolicyDocumentScreen /> },
  ]),
];
